/**
 * @file accreditations.js
 * @description Agency accreditations are SELF-REPORTED checkboxes on the agency
 *   profile (agency_profiles.accreditations). ABAcases does not verify them; the
 *   only agency identity check is the federal NPI registry lookup. This module
 *   owns the canonical option list and normalizes legacy saved values (no DB
 *   migration) so one accreditation never renders twice.
 * @module lib/accreditations
 */

// Canonical values stored on new saves. Order = picker order.
export const ACCREDITATION_OPTIONS = [
  "BHCOE Accredited",
  "CARF Certified",
  "BACB ACE Provider",
  "JCAHO Accredited",
  "Medicaid Provider",
];

// Legacy / variant spellings that already exist in saved profiles, mapped to
// the canonical value. Keys are lowercased and trimmed.
const ALIASES = {
  "bhcoe": "BHCOE Accredited",
  "bhcoe certified": "BHCOE Accredited",
  "bhcoe accredited": "BHCOE Accredited",
};

/**
 * Map one saved value to its canonical form. Unknown values are returned
 * trimmed and otherwise untouched (e.g. a legacy "State Licensed").
 * @param {string} value
 * @returns {string}
 */
export function canonicalAccreditation(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const known = ACCREDITATION_OPTIONS.find(o => o.toLowerCase() === raw.toLowerCase());
  return known || ALIASES[raw.toLowerCase()] || raw;
}

/**
 * Normalize a saved accreditation list: canonicalize legacy values and drop
 * duplicates while preserving first-seen order. Never writes anything.
 * @param {string[]|null|undefined} list
 * @returns {string[]}
 */
export function normalizeAccreditations(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const item of list) {
    const c = canonicalAccreditation(item);
    if (!c || seen.has(c)) continue;
    seen.add(c);
    out.push(c);
  }
  return out;
}
